import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { is, both, complement, equals, test, isNil } from 'ramda'
import { remove } from 'diacritics'
import cc from 'classcat'

class MainInput extends Component {
  render() {
    const {
      inputClass, inputContainerClass, label, onInputChanged, inputType, isDisabled, error, value, returnFullInputEvent, ...rest
    } = this.props

    return (
      <div className={ cc([inputContainerClass, "main-input"]) }>
        { !isNil(label) && this._renderLabel(label) }
        { this._renderInput(inputType, value, onInputChanged, isDisabled, inputClass, returnFullInputEvent, rest) }
        { !isNil(error) && this._renderError(error) }
      </div>
    )
  }

  _renderLabel = label => (
    <div className="main-input-title">
      { label }
    </div>
  )

  _renderInput(inputType, value, onInputChanged, isDisabled, inputClass, returnFullInputEvent, rest) {
    let inputElement = null
    const joinedInputClass = cc([inputClass, "main-input-inputfield"])
    switch (inputType) {
      case "posNumber":
        inputElement = (
          <input
            className={ joinedInputClass }
            type="text"
            value={ value }
            onChange={ this._checkForIllegalCharacters(/[^0-9]/g, onInputChanged, returnFullInputEvent) }
            disabled={ isDisabled }
            onKeyDown={ this._checkForPosNumber }
            pattern="[0-9]"
            { ...rest } />
        )
        break
      case "id":
        inputElement = (
          <input
            className={ joinedInputClass }
            type="text"
            value={ value }
            onChange={ this._checkForIllegalCharacters(/[^a-zA-Z0-9-]/g, onInputChanged, returnFullInputEvent) }
            disabled={ isDisabled }
            onKeyDown={ this._checkForAlphaNumeric }
            pattern="^[a-zA-Z0-9-]*$"
            { ...rest } />
        )
        break
      case "decimal":
        inputElement = (
          <input
            className={ joinedInputClass }
            type="text"
            value={ value }
            onChange={ this._checkForIllegalCharacters(/[^-0-9.]/g, onInputChanged, returnFullInputEvent) }
            disabled={ isDisabled }
            onKeyDown={ this._checkForDecimal }
            pattern="^-?\d*\.?\d*"
            { ...rest } />
        )
        break
      default:
        inputElement = (
          <input
            className={ joinedInputClass }
            type={ inputType }
            value={ value }
            onChange={ this._inputChanged(onInputChanged, returnFullInputEvent) }
            disabled={ isDisabled }
            { ...rest } />
        )
        break
    }
    return inputElement
  }

  _renderError = error => (
    <div className="main-input-error">
      { error }
    </div>
  )

  _inputChanged = (callback, returnFullInputEvent) => (e) => {
    if (returnFullInputEvent) {
      callback(e)
    } else {
      callback(e.target.value)
    }
  }

  _checkForIllegalCharacters = (regex, callback, returnFullInputEvent) => (e) => {
    this._removeDiacriticsAndSpecialCharacters(e, regex)
    if (returnFullInputEvent) {
      callback(e)
    } else {
      callback(e.target.value)
    }
  }

  _checkForPosNumber = (e) => {
    if (this._allowDefaults(e, [46, 8, 9, 27, 13, 110, 190])) {
      return
    }
    const isValidNumber = both(is(Number), complement(equals(NaN)))
    // Ensure that it is a number and stop the keypress, and prevent diacritics
    if (!isValidNumber(+e.key)) {
      e.preventDefault()
    }
  }

  _checkForAlphaNumeric = (e) => {
    if (this._allowDefaults(e, [46, 8, 9, 27, 13, 110, 190])) {
      return
    }
    const isValidCharacter = test(/^[a-zA-Z0-9-]*$/, e.key) //= > false
    // Ensure that it is a number, letter or dash or else stop the keypress
    this._removeDiacriticsAndSpecialCharacters(e, /[^a-zA-Z0-9-]/g)
    if (!isValidCharacter) {
      e.preventDefault()
    }
  }

  _checkForDecimal = (e) => {
    if (this._allowDefaults(e, [46, 8, 9, 27, 13])) {
      return
    }
    const charCode = (e.which) ? e.which : e.keyCode
    const stopAnythingNotAllowed = ((charCode < 48 || charCode > 57) && (charCode < 96 || charCode > 105) && charCode !== 190 && charCode !== 110 && charCode !== 109 && charCode !== 189)
    const stopSecondDecimalPoint = ((charCode === 190 || charCode === 110) && e.target.value.split('.').length > 1)
    const stopDashAfterFirstCharacter = ((charCode === 109 || charCode === 189) && e.target.value.length > 0)
    if (stopAnythingNotAllowed || stopSecondDecimalPoint || stopDashAfterFirstCharacter) {
      e.preventDefault()
    }
  }

  _allowDefaults = (e, keysToAllow) => {
    const charCode = (e.which) ? e.which : e.keyCode
    // Allow: backspace, delete, tab, escape, enter and .
    return (keysToAllow.includes(charCode)
      // Allow: Ctrl+A, Command+A
      || (charCode === 65 && (e.ctrlKey === true || e.metaKey === true))
      // Allow: home, end, left, right, down, up
      || (charCode >= 35 && charCode <= 40))
    // let it happen, don't do anything
  }

  _removeDiacriticsAndSpecialCharacters = (e, regex) => {
    e.target.value = e.target.value.replace(regex, '')
    e.target.value = remove(e.target.value)
  }
}

MainInput.propTypes = {
  error: PropTypes.string,
  inputType: PropTypes.oneOf([
    'button',
    'checkbox',
    'color',
    'date',
    'datetime-local',
    'email',
    'file',
    'hidden',
    'id',
    'image',
    'month',
    'number',
    'decimal', // custom type
    'posNumber', // custom type
    'password',
    'radio',
    'range',
    'reset',
    'search',
    'submit',
    'tel',
    'text',
    'time',
    'url',
    'week']),
  isDisabled: PropTypes.bool,
  inputClass: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
  ]),
  inputContainerClass: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
  ]),
  label: PropTypes.string,
  onInputChanged: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
  returnFullInputEvent: PropTypes.bool,
}

MainInput.defaultProps = {
  error: null,
  inputType: 'text',
  inputClass: [],
  inputContainerClass: [],
  isDisabled: false,
  returnFullInputEvent: false,
  label: '',
}

export default MainInput
